import { cssVars } from '@lmc/design-system/tokens'
import type { NamedPerson } from '@lmc/validations'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import IncidentCaseV2StepForm from '../IncidentCaseV2StepForm'
import type {
  IncidentCaseV2StepProps,
  IncidentCaseV2StepValues
} from '../incidentCaseV2Steps'
import StepNameList from '../StepNameList'
import StepPickerList from '../StepPickerList'
import { StepSection } from '../stepLayout'
import {
  type SavedPerson,
  useServiceUserOptions,
  useTeamMemberOptions,
  withSavedPeople
} from '../stepOptions'
import { toNamedPeople } from '../stepValues'
import { useServiceUserWords } from '../useServiceUserWords'

export type { SavedPerson }

const testId = 'incident-case-v2-people-involved'

type PeopleInvolvedValues = {
  carersInvolved: Array<string | undefined>
  otherPartiesInvolved: Array<{ name?: string }>
  serviceUsersInvolved: Array<string | undefined>
  witnesses: Array<{ name?: string }>
}

/** The people fields the read returns; ts-common's case type lacks the lists. */
export type CasePeople = {
  carersInvolved?: Array<SavedPerson> | null
  otherPartiesInvolved?: Array<NamedPerson>
  otherPeopleInvolved?: string | null
  otherWitnesses?: string | null
  serviceUsersInvolved?: Array<SavedPerson> | null
  witnesses?: Array<NamedPerson>
}

const savedIds = (people: Array<SavedPerson> | null | undefined) =>
  (people ?? []).flatMap((person) => (person ? [person.id] : []))

/**
 * The named list, or, until it has been saved, the free text a draft case may
 * carry instead (the mobile questionnaire records other people that way), as
 * one row the user can edit or split.
 */
export const savedNames = (
  list: Array<NamedPerson> | undefined,
  freeText: string | null | undefined
): Array<{ name?: string }> =>
  list ?? (freeText?.trim() ? [{ name: freeText.trim() }] : [])

/** Picked ids in order, without empty rows or repeats. */
const toIds = (ids: ReadonlyArray<string | undefined>) => [
  ...new Set(ids.filter((id): id is string => Boolean(id)))
]

const toStepValues = (
  values: PeopleInvolvedValues
): IncidentCaseV2StepValues => ({
  carersInvolved: toIds(values.carersInvolved ?? []),
  otherPartiesInvolved: toNamedPeople(values.otherPartiesInvolved),
  serviceUsersInvolved: toIds(values.serviceUsersInvolved ?? []),
  witnesses: toNamedPeople(values.witnesses)
})

/**
 * Step 3: everyone else involved in the incident — other service users and
 * team members picked from the home, and other parties and witnesses by name.
 * Nothing here is required.
 */
const PeopleInvolvedStep = ({
  incidentReport,
  handleRef,
  onDirtyChange
}: IncidentCaseV2StepProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { plural, singular } = useServiceUserWords()
  const people = useMemo(
    () => (incidentReport.case ?? {}) as CasePeople,
    [incidentReport.case]
  )

  // Someone saved who has since left stays named on their row, but once the
  // row is removed they can't be picked again.
  const serviceUserOptions = withSavedPeople(
    useServiceUserOptions({ excludeId: incidentReport.serviceUser.id }),
    people.serviceUsersInvolved,
    { isLocked: true }
  )
  const teamMemberOptions = withSavedPeople(
    useTeamMemberOptions(),
    people.carersInvolved,
    { isLocked: true }
  )

  const initialValues = useMemo<PeopleInvolvedValues>(
    () => ({
      carersInvolved: savedIds(people.carersInvolved),
      otherPartiesInvolved: savedNames(
        people.otherPartiesInvolved,
        people.otherPeopleInvolved
      ),
      serviceUsersInvolved: savedIds(people.serviceUsersInvolved),
      witnesses: savedNames(people.witnesses, people.otherWitnesses)
    }),
    [people]
  )

  return (
    <IncidentCaseV2StepForm
      handleRef={handleRef}
      onDirtyChange={onDirtyChange}
      initialValues={initialValues}
      toStepValues={toStepValues}
      testId={testId}
    >
      <Groups>
        <StepSection
          title={t(
            incidentReportsConstants.CASE_V2_PEOPLE_OTHER_SERVICE_USERS,
            { variable: plural }
          )}
        >
          <StepPickerList
            addLabel={t(
              incidentReportsConstants.CASE_V2_PEOPLE_ADD_OTHER_SERVICE_USER,
              { variable: singular }
            )}
            itemLabel={t(
              incidentReportsConstants.CASE_V2_PEOPLE_OTHER_SERVICE_USER,
              { variable: singular }
            )}
            name="serviceUsersInvolved"
            options={serviceUserOptions}
            testId={`${testId}-service-users`}
          />
        </StepSection>

        <StepSection
          title={t(incidentReportsConstants.CASE_V2_PEOPLE_TEAM_MEMBERS)}
        >
          <StepPickerList
            addLabel={t(
              incidentReportsConstants.CASE_V2_PEOPLE_ADD_TEAM_MEMBER
            )}
            itemLabel={t(incidentReportsConstants.CASE_V2_PEOPLE_TEAM_MEMBER)}
            name="carersInvolved"
            options={teamMemberOptions}
            testId={`${testId}-team-members`}
          />
        </StepSection>

        <StepSection
          title={t(incidentReportsConstants.CASE_V2_PEOPLE_OTHER_PARTIES)}
        >
          <StepNameList
            addLabel={t(incidentReportsConstants.CASE_V2_PEOPLE_ADD_PARTY)}
            name="otherPartiesInvolved"
            testId={`${testId}-other-parties`}
          />
        </StepSection>

        <StepSection
          title={t(incidentReportsConstants.CASE_V2_PEOPLE_WITNESSES)}
        >
          <StepNameList
            addLabel={t(incidentReportsConstants.CASE_V2_PEOPLE_ADD_WITNESS)}
            name="witnesses"
            testId={`${testId}-witnesses`}
          />
        </StepSection>
      </Groups>
    </IncidentCaseV2StepForm>
  )
}

/** The design sets these groups 16px apart, closer than other steps' groups. */
const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssVars.spacing[200]};
`

export default PeopleInvolvedStep
